import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { 
  Plus, Phone, Mail, Package, Loader2, Trash2, 
  Check, Clock, AlertCircle, Search, X, Calendar, 
  PhoneCall, ShoppingBag, ArrowRight, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomerOrders = ({ view = 'to-order' }) => {
  const { token, user, activeStaff } = useAuth();
  const { selectedStore, storeName } = useStore();
  const [orders, setOrders] = useState([]);

  // Apply a per-item status change in local state without refetching
  // (refetch caused the page to scroll/jump while staff were updating items).
  const patchItemStatusLocal = (orderId, itemIdx, newStatus) => {
    setOrders(prev => prev.map(o => {
      if (o.id !== orderId) return o;
      const item_lines = (o.item_lines || []).map((il, i) => i === itemIdx ? { ...il, status: newStatus } : il);
      return { ...o, item_lines };
    }));
  };
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [markingArrived, setMarkingArrived] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showBin, setShowBin] = useState(false);
  const [binOrders, setBinOrders] = useState([]);
  const [binLoading, setBinLoading] = useState(false);

  // Owner = Ritu (via staff PIN) or direct account login (no staff PIN active)
  const isOwner = !activeStaff || (activeStaff?.name || '').trim().toLowerCase() === 'ritu';

  const [form, setForm] = useState({
    customer_name: '',
    email: '',
    phone: '',
    items: '',
    notes: '',
    store: 'orewa',
    order_date: new Date().toISOString().split('T')[0],
    supplier_name: '',
  });

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer-orders`, { headers: getAuthHeader() });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load customer orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  useEffect(() => {
    axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() })
      .then(res => setSuppliers((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    if (!form.customer_name.trim() || !form.items.trim()) {
      toast.error('Customer name and products are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        staff_name: activeStaff?.name || user?.name || user?.email || '',
      };
      await axios.post(`${API_URL}/customer-orders`, payload, { headers: getAuthHeader() });
      toast.success('Customer order created');
      setShowCreateDialog(false);
      setForm({ customer_name: '', email: '', phone: '', items: '', notes: '', store: 'orewa', order_date: new Date().toISOString().split('T')[0], supplier_name: '' });
      fetchOrders();
    } catch (error) {
      toast.error('Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkArrived = async (orderId) => {
    setMarkingArrived(orderId);
    try {
      const response = await axios.post(`${API_URL}/customer-orders/${orderId}/mark-arrived`, {}, { headers: getAuthHeader() });
      if (response.data.email_sent) {
        toast.success('Customer notified by email!');
      } else {
        toast.warning(response.data.message || 'Marked arrived - call customer');
      }
      fetchOrders();
    } catch (error) {
      toast.error('Failed to mark as arrived');
    } finally {
      setMarkingArrived(null);
    }
  };

  const handleMarkCalled = async (orderId) => {
    try {
      await axios.put(`${API_URL}/customer-orders/${orderId}`, { status: 'called' }, { headers: getAuthHeader() });
      toast.success('Marked as called');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handlePickedUp = async (orderId) => {
    try {
      await axios.post(`${API_URL}/customer-orders/${orderId}/picked-up`, {}, { headers: getAuthHeader() });
      toast.success('Picked up — moved to history');
      fetchOrders();
      if (showHistory) fetchHistory();
    } catch (error) {
      toast.error('Failed to mark as picked up');
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/customer-orders?status=picked_up`, { headers: getAuthHeader() });
      setHistoryOrders(res.data || []);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) fetchHistory();
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Move this customer order to the recycle bin? It can be restored later.')) return;
    try {
      await axios.delete(`${API_URL}/customer-orders/${orderId}`, { headers: getAuthHeader() });
      toast.success('Order moved to the recycle bin');
      fetchOrders();
      if (showHistory) fetchHistory();
      if (showBin) fetchBin();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to delete';
      toast.error(msg);
    }
  };

  const fetchBin = async () => {
    setBinLoading(true);
    try {
      const res = await axios.get(`${API_URL}/customer-orders/deleted`, { headers: getAuthHeader() });
      setBinOrders(res.data || []);
    } catch (error) {
      toast.error('Failed to load recycle bin');
    } finally {
      setBinLoading(false);
    }
  };

  const toggleBin = () => {
    const next = !showBin;
    setShowBin(next);
    if (next) fetchBin();
  };

  const handleRestore = async (orderId) => {
    try {
      await axios.post(`${API_URL}/customer-orders/${orderId}/restore`, {}, { headers: getAuthHeader() });
      toast.success('Order restored');
      fetchOrders();
      fetchBin();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore');
    }
  };

  const handleUpdate = async () => {
    if (!editOrder) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/customer-orders/${editOrder.id}`, {
        customer_name: editOrder.customer_name,
        email: editOrder.email,
        phone: editOrder.phone,
        items: editOrder.items,
        notes: editOrder.notes,
        store: editOrder.store,
        supplier_name: editOrder.supplier_name || '',
      }, { headers: getAuthHeader() });
      toast.success('Order updated');
      setEditOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Split orders into two groups — filtered by active store
  const storeOrders = orders.filter(o => !o.store || o.store === selectedStore);
  
  // Left = has any item still needing to be ordered
  const toOrderList = storeOrders
    .filter(o => {
      if (o.item_lines && o.item_lines.length > 0) {
        return o.item_lines.some(il => il.status === 'not_ordered' || il.status === 'oos');
      }
      return o.status === 'not_ordered' || o.status === 'oos';
    })
    .filter(o => !searchTerm || o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) || o.items?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Right = all items ordered or beyond
  const orderedList = storeOrders
    .filter(o => {
      if (o.item_lines && o.item_lines.length > 0) {
        return o.item_lines.every(il => il.status !== 'not_ordered' && il.status !== 'oos');
      }
      return o.status === 'ordered' || o.status === 'arrived' || o.status === 'called';
    })
    .filter(o => !searchTerm || o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) || o.items?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Compute aggregated/effective status: when item_lines exist, derive from them
  // so the top-level pill reflects the real state (e.g. all items ordered ⇒ "Ordered").
  const getEffectiveStatus = (order) => {
    if (!order.item_lines || order.item_lines.length === 0) return order.status;
    const statuses = order.item_lines.map(il => il.status);
    if (statuses.every(s => s === 'arrived')) return 'arrived';
    if (statuses.every(s => s === 'called')) return 'called';
    if (statuses.every(s => s === 'oos')) return 'oos';
    if (statuses.every(s => s !== 'not_ordered' && s !== 'oos')) return 'ordered'; // all ordered/arrived/called mix
    if (statuses.some(s => s === 'ordered' || s === 'arrived' || s === 'called')) return 'partial';
    return 'not_ordered';
  };

  // Render a compact order card
  const OrderCard = ({ order, side }) => {
    const effStatus = getEffectiveStatus(order);
    return (
    <Card className={`border-l-4 ${
      effStatus === 'not_ordered' ? 'border-l-red-400' : 
      effStatus === 'oos' ? 'border-l-red-700' : 
      effStatus === 'ordered' ? 'border-l-amber-400' : 
      effStatus === 'partial' ? 'border-l-amber-400' :
      effStatus === 'called' ? 'border-l-blue-400' :
      'border-l-green-400'
    }`} data-testid={`customer-order-${order.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-slate-900 text-xl">{order.customer_name}</h3>
              {order.supplier_name && <Badge className="bg-indigo-100 text-indigo-700 text-sm px-2 font-semibold">{order.supplier_name}</Badge>}
              {/* Top-level status badge — always shown, derived from item_lines when present
                  so staff can see at a glance: Ordered / Partial / Arrived / OOS / Called. */}
              {effStatus === 'oos' && <Badge className="bg-red-700 text-white text-base px-3 py-0.5 font-bold">OOS</Badge>}
              {effStatus === 'ordered' && <Badge className="bg-amber-100 text-amber-800 text-base px-3 py-0.5 font-bold border border-amber-300">Ordered</Badge>}
              {effStatus === 'partial' && <Badge className="bg-amber-50 text-amber-700 text-base px-3 py-0.5 font-bold border border-amber-300">Partially Ordered</Badge>}
              {effStatus === 'called' && <Badge className="bg-blue-100 text-blue-800 text-base px-3 py-0.5 font-bold border border-blue-300">Called</Badge>}
              {effStatus === 'arrived' && <Badge className="bg-green-100 text-green-800 text-base px-3 py-0.5 font-bold border border-green-300">Arrived</Badge>}
              {order.arrived_via_po && effStatus === 'arrived' && <span className="text-xs text-green-700" data-testid={`arrived-via-po-${order.id}`}>via {order.arrived_via_po.supplier || 'supplier'} delivery{order.notified_at ? '' : ' · not yet texted'}</span>}
              {order.auto_ordered_from_po && effStatus === 'ordered' && <span className="text-xs text-amber-700" data-testid={`ordered-via-po-${order.id}`}>on {order.auto_ordered_from_po.supplier || 'supplier'} PO</span>}
            </div>
            {/* Individual item tracking */}
            {order.item_lines && order.item_lines.length > 0 ? (
              <div className="mt-1 space-y-1">
                {order.item_lines.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-slate-50 rounded px-2 py-2">
                    <span className={`text-base font-semibold ${item.status === 'ordered' ? 'text-amber-700' : item.status === 'arrived' ? 'text-green-700 line-through' : item.status === 'oos' ? 'text-red-600' : 'text-slate-800'}`}>
                      {item.name}
                    </span>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      {item.status === 'not_ordered' && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={async (e) => { e.stopPropagation(); patchItemStatusLocal(order.id, idx, 'ordered'); try { await axios.put(`${API_URL}/customer-orders/${order.id}/item/${idx}`, { status: 'ordered' }, { headers: getAuthHeader() }); } catch (err) { fetchOrders(); } }}
                        >Ordered</Button>
                      )}
                      {item.status === 'not_ordered' && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50"
                          onClick={async (e) => { e.stopPropagation(); patchItemStatusLocal(order.id, idx, 'oos'); try { await axios.put(`${API_URL}/customer-orders/${order.id}/item/${idx}`, { status: 'oos' }, { headers: getAuthHeader() }); } catch (err) { fetchOrders(); } }}
                        >OOS</Button>
                      )}
                      {item.status === 'ordered' && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-green-300 text-green-600 hover:bg-green-50"
                          onClick={async (e) => { e.stopPropagation(); patchItemStatusLocal(order.id, idx, 'arrived'); try { await axios.put(`${API_URL}/customer-orders/${order.id}/item/${idx}`, { status: 'arrived' }, { headers: getAuthHeader() }); } catch (err) { fetchOrders(); } }}
                        >Arrived</Button>
                      )}
                      {item.status === 'oos' && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={async (e) => { e.stopPropagation(); patchItemStatusLocal(order.id, idx, 'ordered'); try { await axios.put(`${API_URL}/customer-orders/${order.id}/item/${idx}`, { status: 'ordered' }, { headers: getAuthHeader() }); } catch (err) { fetchOrders(); } }}
                        >Reorder</Button>
                      )}
                      <Badge className={`text-xs px-1.5 ${
                        item.status === 'ordered' ? 'bg-amber-100 text-amber-700' : 
                        item.status === 'arrived' ? 'bg-green-100 text-green-700' : 
                        item.status === 'oos' ? 'bg-red-100 text-red-700' : 
                        'bg-slate-100 text-slate-600'
                      }`}>{item.status === 'not_ordered' ? 'Pending' : item.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-lg font-semibold text-slate-800">{order.items}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-base text-slate-600">
              {order.phone && <span className="flex items-center gap-1 font-semibold"><Phone className="w-4 h-4" />{order.phone}</span>}
              <span className="font-semibold">{formatDate(order.order_date || order.created_at)}</span>
              {order.store && <span className="uppercase font-semibold">{order.store}</span>}
              {order.created_by && (
                <span className="flex items-center gap-1 text-slate-700" data-testid={`order-staff-${order.id}`}>
                  By <strong className="text-slate-900">{order.created_by}</strong>
                </span>
              )}
            </div>
            {order.notes && <p className="text-base text-slate-600 italic mt-1" data-testid={`order-notes-${order.id}`}>{order.notes}</p>}
            {order.edited_by && (
              <p className="text-xs text-slate-400 mt-1" data-testid={`order-edited-${order.id}`}>Edited by {order.edited_by} · {formatDate(order.edited_at)}</p>
            )}
            {order.status === 'oos' && order.oos_date && (
              <p className="text-base text-red-600 font-semibold mt-1">OOS since: {formatDate(order.oos_date)}</p>
            )}
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            {/* LEFT SIDE: To Order */}
            {side === 'left' && order.status === 'not_ordered' && (
              <Button size="sm" variant="outline" className="text-sm font-semibold border-amber-300 text-amber-700 hover:bg-amber-50 h-8 px-3"
                onClick={async () => {
                  await axios.put(`${API_URL}/customer-orders/${order.id}`, { status: 'ordered' }, { headers: getAuthHeader() });
                  toast.success('Moved to Ordered');
                  fetchOrders();
                }}
                data-testid={`mark-ordered-${order.id}`}
              >
                <ArrowRight className="w-4 h-4 mr-1" /> Ordered
              </Button>
            )}
            {side === 'left' && order.status === 'not_ordered' && (
              <Button size="sm" variant="outline" className="text-sm font-semibold border-red-300 text-red-600 hover:bg-red-50 h-8 px-3"
                onClick={async () => {
                  await axios.put(`${API_URL}/customer-orders/${order.id}`, { status: 'oos', oos_date: new Date().toISOString() }, { headers: getAuthHeader() });
                  toast.warning('Marked OOS');
                  fetchOrders();
                }}
                data-testid={`mark-oos-${order.id}`}
              >
                <AlertCircle className="w-4 h-4 mr-1" /> OOS
              </Button>
            )}
            {side === 'left' && order.status === 'oos' && (
              <Button size="sm" variant="outline" className="text-sm font-semibold border-amber-300 text-amber-700 hover:bg-amber-50 h-8 px-3"
                onClick={async () => {
                  await axios.put(`${API_URL}/customer-orders/${order.id}`, { status: 'ordered' }, { headers: getAuthHeader() });
                  toast.success('Reordered');
                  fetchOrders();
                }}
                data-testid={`reorder-${order.id}`}
              >
                <ArrowRight className="w-4 h-4 mr-1" /> Reorder
              </Button>
            )}

            {/* RIGHT SIDE: Ordered */}
            {side === 'right' && order.status === 'ordered' && (
              <Button size="sm" className="text-sm font-semibold bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                onClick={() => handleMarkArrived(order.id)}
                disabled={markingArrived === order.id}
                data-testid={`mark-arrived-${order.id}`}
              >
                {markingArrived === order.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Arrived
              </Button>
            )}
            {side === 'right' && order.status === 'ordered' && (
              <Button size="sm" variant="outline" className="text-sm font-semibold border-red-300 text-red-600 hover:bg-red-50 h-8 px-3"
                onClick={async () => {
                  await axios.put(`${API_URL}/customer-orders/${order.id}`, { status: 'oos', oos_date: new Date().toISOString() }, { headers: getAuthHeader() });
                  toast.warning('Marked OOS');
                  fetchOrders();
                }}
                data-testid={`oos-ordered-${order.id}`}
              >
                <AlertCircle className="w-4 h-4 mr-1" /> OOS
              </Button>
            )}
            {side === 'right' && order.status === 'arrived' && !order.notified_at && (
              <Button size="sm" className="text-sm font-semibold bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                onClick={() => handleMarkArrived(order.id)}
                disabled={markingArrived === order.id}
                title="Text the customer that their order is in"
                data-testid={`text-customer-${order.id}`}
              >
                {markingArrived === order.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-1" />} Text customer
              </Button>
            )}
            {side === 'right' && (order.status === 'arrived') && (
              <Button size="sm" variant="outline" className="text-sm font-semibold border-blue-300 text-blue-700 hover:bg-blue-50 h-8 px-3"
                onClick={() => handleMarkCalled(order.id)}
                data-testid={`mark-called-${order.id}`}
              >
                <PhoneCall className="w-4 h-4 mr-1" /> Called
              </Button>
            )}
            {side === 'right' && order.status === 'called' && (
              <Button size="sm" className="text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3"
                onClick={() => handlePickedUp(order.id)}
                data-testid={`picked-up-${order.id}`}
              >
                <ShoppingBag className="w-4 h-4 mr-1" /> Picked Up
              </Button>
            )}

            {/* Common */}
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-sm text-slate-500 hover:text-slate-700" onClick={() => setEditOrder({...order})} data-testid={`edit-order-${order.id}`}>
                Edit
              </Button>
              {/* Delete: staff only while still pending (typo/duplicate fixes). Owner can always delete.
                  Once ordered, the only way off the list is "Picked Up". */}
              {(isOwner || effStatus === 'not_ordered' || effStatus === 'oos') && (
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDelete(order.id)} data-testid={`delete-order-${order.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="customer-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Customer Orders — {storeName(selectedStore)}</h1>
          <p className="text-sm text-slate-500">Left: needs ordering | Right: ordered & waiting for pickup</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-48"
              data-testid="customer-orders-search"
            />
          </div>
          <Button onClick={() => { setForm({ ...form, store: selectedStore }); setShowCreateDialog(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="create-customer-order-btn">
            <Plus className="w-4 h-4 mr-2" /> New Order
          </Button>
        </div>
      </div>

      {/* Single-Panel Layout — view-controlled */}
      <div className="grid grid-cols-1 gap-4">
        {view === 'to-order' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <h2 className="font-bold text-lg text-slate-800">To Order</h2>
            <Badge variant="outline" className="text-xs">{toOrderList.length}</Badge>
            <Button size="sm" variant="outline" className="ml-auto text-sm font-semibold h-8 px-3" onClick={toggleBin} data-testid="toggle-recycle-bin-btn-toorder">
              <Trash2 className="w-4 h-4 mr-1" /> {showBin ? 'Hide Bin' : 'Recycle Bin'}
            </Button>
          </div>
          <div className="space-y-2 max-h-[78vh] overflow-y-auto pr-1">
            {toOrderList.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <Check className="w-8 h-8 mx-auto mb-2 text-green-400" />
                All caught up!
              </div>
            ) : (
              toOrderList.map(order => <OrderCard key={order.id} order={order} side="left" />)
            )}
          </div>
          {showBin && (
            <div className="mt-6" data-testid="recycle-bin-section-toorder">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <h2 className="font-bold text-lg text-slate-800">Recycle Bin</h2>
                <Badge variant="outline" className="text-xs">{binOrders.length}</Badge>
              </div>
              {binLoading ? (
                <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
              ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {binOrders.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-sm">Recycle bin is empty</div>
                  ) : (
                    binOrders.map(order => (
                      <Card key={order.id} className="border-l-4 border-l-red-400 bg-red-50/30" data-testid={`bin-order-${order.id}`}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-slate-900 text-lg">{order.customer_name}</h3>
                                <Badge className="bg-red-100 text-red-700 text-sm px-2 font-bold border border-red-300">Deleted</Badge>
                              </div>
                              <p className="text-base text-slate-700 whitespace-pre-line">{order.items}</p>
                              <div className="flex items-center gap-4 mt-1 text-sm text-slate-600 flex-wrap">
                                {order.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{order.phone}</span>}
                                {order.store && <span className="uppercase font-semibold">{order.store}</span>}
                                <span>Deleted <strong className="text-slate-900">{order.deleted_at ? formatDateTime(order.deleted_at) : ''}</strong>{order.deleted_by ? <> by <strong className="text-red-700">{order.deleted_by}</strong></> : null}</span>
                              </div>
                            </div>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 shrink-0" onClick={() => handleRestore(order.id)} data-testid={`restore-order2-${order.id}`}>
                              <ArrowRight className="w-4 h-4 mr-1 rotate-180" /> Restore
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {view === 'ordered' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-3 h-3 rounded-full bg-amber-400" />
            <h2 className="font-bold text-lg text-slate-800">Ordered & Waiting</h2>
            <Badge variant="outline" className="text-xs">{orderedList.length}</Badge>
            <Button size="sm" variant="outline" className="ml-auto text-sm font-semibold h-8 px-3" onClick={toggleHistory} data-testid="toggle-pickup-history-btn">
              <Clock className="w-4 h-4 mr-1" /> {showHistory ? 'Hide' : 'Picked Up History'}
            </Button>
            <Button size="sm" variant="outline" className="text-sm font-semibold h-8 px-3" onClick={toggleBin} data-testid="toggle-recycle-bin-btn">
              <Trash2 className="w-4 h-4 mr-1" /> {showBin ? 'Hide Bin' : 'Recycle Bin'}
            </Button>
          </div>
          <div className="space-y-2 max-h-[78vh] overflow-y-auto pr-1">
            {orderedList.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No orders waiting
              </div>
            ) : (
              orderedList.map(order => <OrderCard key={order.id} order={order} side="right" />)
            )}
          </div>

          {/* Picked Up History — read-only record of completed orders */}
          {showHistory && (
            <div className="mt-6" data-testid="pickup-history-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <h2 className="font-bold text-lg text-slate-800">Picked Up History</h2>
                <Badge variant="outline" className="text-xs">{historyOrders.filter(o => !o.store || o.store === selectedStore).length}</Badge>
              </div>
              {historyLoading ? (
                <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
              ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {historyOrders.filter(o => !o.store || o.store === selectedStore).length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-sm">No picked-up orders yet</div>
                  ) : (
                    historyOrders.filter(o => !o.store || o.store === selectedStore).map(order => (
                      <Card key={order.id} className="border-l-4 border-l-emerald-500 bg-emerald-50/30" data-testid={`history-order-${order.id}`}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-slate-900 text-lg">{order.customer_name}</h3>
                                <Badge className="bg-emerald-100 text-emerald-800 text-sm px-2 font-bold border border-emerald-300">Picked Up</Badge>
                              </div>
                              <p className="text-base text-slate-700 whitespace-pre-line">{order.items}</p>
                              <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                                {order.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{order.phone}</span>}
                                {order.store && <span className="uppercase font-semibold">{order.store}</span>}
                                <span>Picked up <strong className="text-slate-900">{order.picked_up_at ? formatDateTime(order.picked_up_at) : ''}</strong>{order.picked_up_by ? <> by <strong className="text-slate-900">{order.picked_up_by}</strong></> : null}</span>
                              </div>
                            </div>
                            {isOwner && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 shrink-0" onClick={() => handleDelete(order.id)} data-testid={`delete-history-${order.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Recycle Bin — soft-deleted orders, restorable, shows who deleted */}
          {showBin && (
            <div className="mt-6" data-testid="recycle-bin-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <h2 className="font-bold text-lg text-slate-800">Recycle Bin</h2>
                <Badge variant="outline" className="text-xs">{binOrders.length}</Badge>
              </div>
              {binLoading ? (
                <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
              ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {binOrders.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-sm">Recycle bin is empty</div>
                  ) : (
                    binOrders.map(order => (
                      <Card key={order.id} className="border-l-4 border-l-red-400 bg-red-50/30" data-testid={`bin-order-${order.id}`}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-slate-900 text-lg">{order.customer_name}</h3>
                                <Badge className="bg-red-100 text-red-700 text-sm px-2 font-bold border border-red-300">Deleted</Badge>
                              </div>
                              <p className="text-base text-slate-700 whitespace-pre-line">{order.items}</p>
                              <div className="flex items-center gap-4 mt-1 text-sm text-slate-600 flex-wrap">
                                {order.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{order.phone}</span>}
                                {order.store && <span className="uppercase font-semibold">{order.store}</span>}
                                <span>Deleted <strong className="text-slate-900">{order.deleted_at ? formatDateTime(order.deleted_at) : ''}</strong>{order.deleted_by ? <> by <strong className="text-red-700">{order.deleted_by}</strong></> : null}</span>
                              </div>
                            </div>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 shrink-0" onClick={() => handleRestore(order.id)} data-testid={`restore-order-${order.id}`}>
                              <ArrowRight className="w-4 h-4 mr-1 rotate-180" /> Restore
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">New Customer Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Customer Name *</label>
              <Input value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} placeholder="Customer name" data-testid="co-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="customer@email.com" data-testid="co-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Phone</label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="021 123 4567" data-testid="co-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Store</label>
                <select
                  value={form.store}
                  onChange={(e) => setForm({...form, store: e.target.value})}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                >
                  <option value="orewa">{storeName('orewa')}</option>
                  <option value="eastridge">{storeName('eastridge')}</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Order Date</label>
                <Input type="date" value={form.order_date} onChange={(e) => setForm({...form, order_date: e.target.value})} data-testid="co-order-date" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Supplier</label>
              <select
                value={form.supplier_name}
                onChange={(e) => setForm({...form, supplier_name: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="co-supplier"
              >
                <option value="">— Not specified (will use fuzzy match) —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">Optional — for your records.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Products Requested *</label>
              <textarea
                value={form.items}
                onChange={(e) => setForm({...form, items: e.target.value})}
                placeholder="Enter one product per line, e.g:&#10;Solgar Vitamin D3&#10;Solgar Zinc 50mg&#10;Solgar B-Complex"
                rows={4}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
                data-testid="co-items"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="co-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editOrder} onOpenChange={(open) => !open && setEditOrder(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Customer Order</DialogTitle>
          </DialogHeader>
          {editOrder && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Customer Name</label>
                <Input value={editOrder.customer_name || ''} onChange={(e) => setEditOrder({...editOrder, customer_name: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Email</label>
                  <Input value={editOrder.email || ''} onChange={(e) => setEditOrder({...editOrder, email: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Phone</label>
                  <Input value={editOrder.phone || ''} onChange={(e) => setEditOrder({...editOrder, phone: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Products Requested <span className="text-slate-400 font-normal">— one per line; change, add or remove lines</span></label>
                <textarea
                  value={editOrder.items || ''}
                  onChange={(e) => setEditOrder({...editOrder, items: e.target.value})}
                  rows={5}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-base resize-none"
                  data-testid="co-edit-items"
                />
                <p className="text-xs text-slate-500 mt-1">Lines that stay the same keep their Ordered / Arrived status. New lines start as Pending.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Supplier</label>
                <select
                  value={editOrder.supplier_name || ''}
                  onChange={(e) => setEditOrder({...editOrder, supplier_name: e.target.value})}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="co-edit-supplier"
                >
                  <option value="">— Not specified —</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Notes</label>
                <Input value={editOrder.notes || ''} onChange={(e) => setEditOrder({...editOrder, notes: e.target.value})} data-testid="co-edit-notes" />
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditOrder(null)} data-testid="co-edit-cancel">Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving || !(editOrder?.items || '').trim() || !(editOrder?.customer_name || '').trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="co-edit-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerOrders;
